import * as React from 'react';
import { ContainerContext, mapProps } from './index';
import {
    BaseCloudBuildContainer,
    Button,
    Table,
    CloudBuildFilter,
    StartBuildDialog,
    BaseElement,
    HeaderButton,
} from '../components';
import { UserTitleRole } from '../enums';
import { connect } from 'react-redux';
import actions, { ActionRequest } from '../actions';
import { formatDate, formatDuration } from '../utils';
import {
    CheckCircle,
    Error as ErrorIcon,
    RemoveCircle,
    PlayArrow,
    MoreHoriz,
    Cancel as CancelIcon,
    GetApp as GetAppIcon,
    PlayArrowOutlined,
    CheckCircleOutlined,
    ErrorOutlineOutlined,
    RemoveCircleOutlineOutlined,
} from '@mui/icons-material';
import { Chip, CircularProgress, Grid2 as Grid, IconButton, Menu, MenuItem } from '@mui/material';
import { default as moment } from 'moment';
import * as _ from 'lodash';
import { styled } from '@mui/styles';
import { useNavigate } from 'react-router';
import { Column } from 'material-table';
import { StateMap } from '../reducers';
import useMediaQuery from '@mui/material/useMediaQuery';

interface Props extends ContainerContext.Props {
    jobs: Entities.CloudBuildJob[];
}

type DialogType = 'Build';
interface State {
    readonly openDialog: DialogType | null;
    readonly authAnchorEl: HTMLElement | undefined;
    readonly selectedJob: string;
    readonly selectedDetails: Entities.CloudBuildJobExecution | null;
    readonly successFilter: boolean;
    readonly buildingFilter: boolean;
    readonly canceledFilter: boolean;
    readonly failedFilter: boolean;
}

class Container extends BaseElement<Props, State> {
    state: State = {
        authAnchorEl: undefined,
        openDialog: null,
        selectedJob: '',
        selectedDetails: null,
        successFilter: false,
        buildingFilter: false,
        canceledFilter: false,
        failedFilter: false,
    };

    private tableRef: any;
    private timer: NodeJS.Timeout | null = null;

    async componentDidMount() {
        await actions.devOps.getCloudBuildDetails();
        this.startTimer();
    }

    componentWillUnmount() {
        this.stopTimer();
    }

    protected renderContainer(): React.JSX.Element {
        const { jobs } = this.props;
        let buttons: HeaderButton[] | undefined;
        if (this.props.userTitleRole > UserTitleRole.Viewer) {
            buttons = [];
            if (jobs.length > 0) {
                buttons.push({ text: 'Build', onClick: this.openBuildDialog});
            }
        }
        return (
            <BaseCloudBuildContainer
                {...this.props}
                styled={this.props.styled}
                buttons = {buttons}
            >
                {this.renderContent()}
            </BaseCloudBuildContainer>
        );
    }

    protected renderDialogs(): React.JSX.Element {
        return (
            <>
                {this.renderBuildDialog()}
                {this.renderBuildDetailsMenu()}
            </>
        );
    }

    private renderContent = () => {
        const { jobs } = this.props;
        const { selectedJob, successFilter, buildingFilter, canceledFilter, failedFilter } = this.state;
        const columns: Column<any>[] = [
            { title: 'Status & Build #', field: 'details'},
            { title: 'Build target', field: 'target'},
            { title: 'Branch', field: 'branch'},
            { title: 'Build time', field: 'duration', render: rowData => formatDuration(rowData.duration)},
            { title: '', field: 'actions', cellStyle: { textAlign: 'right' }},
        ];

        this.tableRef = React.createRef();

        const onFilter = (job: string, success: boolean, building: boolean, canceled: boolean, failed: boolean) => {
            this.setState({
                selectedJob: job,
                successFilter: success,
                buildingFilter: building,
                canceledFilter: canceled,
                failedFilter: failed,
            });
            this.onSearch();
        };

        const onResetSelectedJob = () => {
            this.setState({ selectedJob: '' });
            this.onSearch();
        };

        const onResetSuccess = () => {
            this.setState({ successFilter: false });
            this.onSearch();
        };

        const onResetBuilding = () => {
            this.setState({ buildingFilter: false });
            this.onSearch();
        };

        const onResetCanceled = () => {
            this.setState({ canceledFilter: false });
            this.onSearch();
        };

        const onResetFailed = () => {
            this.setState({ failedFilter: false });
            this.onSearch();
        };

        const showChips = selectedJob !== '' || successFilter || buildingFilter || canceledFilter || failedFilter;
        const title =  (
            <Grid container={true} spacing={1}>
                <Grid size={12}>
                    <CloudBuildFilter
                        styled={this.props.styled}
                        jobs={jobs.map(j => j.name)}
                        selectedJob={selectedJob}
                        successFilter={successFilter}
                        buildingFilter={buildingFilter}
                        canceledFilter={canceledFilter}
                        failedFilter={failedFilter}
                        onFilter={onFilter}
                    />
                </Grid>
                {showChips &&
                    <Grid size={12}>
                        <div style={{ display: 'flex', justifyContent: 'flex-start', flexWrap: 'wrap', gridColumnGap: 5, gridGap: 5}} >
                            {selectedJob !== '' && (
                                <Chip
                                    size={'small'}
                                    label={`Target: "${selectedJob}"`}
                                    onDelete={onResetSelectedJob}
                                />
                            )}
                            {successFilter && (
                                <Chip
                                    size={'small'}
                                    icon={<CheckCircleOutlined />}
                                    label='Status: "Success"'
                                    onDelete={onResetSuccess}
                                />
                            )}
                            {buildingFilter && (
                                <Chip
                                    size={'small'}
                                    icon={<PlayArrowOutlined />}
                                    label='Status: "Building"'
                                    onDelete={onResetBuilding}
                                />
                            )}
                            {canceledFilter && (
                                <Chip
                                    size={'small'}
                                    icon={<RemoveCircleOutlineOutlined />}
                                    label='Status: "Canceled"'
                                    onDelete={onResetCanceled}
                                />
                            )}
                            {failedFilter && (
                                <Chip
                                    size={'small'}
                                    icon={<ErrorOutlineOutlined />}
                                    label='Status: "Failed"'
                                    onDelete={onResetFailed}
                                />
                            )}
                        </div>
                    </Grid>
                }
            </Grid>
        );

        return (
            <Table
                styled={this.props.styled}
                tableRef={this.tableRef}
                refreshSeconds={60}
                title={title}
                onRefresh={this.onRefreshData}
                data={this.getExecutionHistory}
                columns={columns}
                options={{
                    selection: false,
                    paging: true,
                    pageSize: 10,
                    pageSizeOptions: [5, 10, 30, 50, 100],
                    search: false,
                    sorting: false,
                    draggable: false,
                    actionsColumnIndex: -1,
                    emptyRowsWhenPaging: false,
                }}
                detailPanel={[
                    rowData => ({
                        disabled: rowData.job.result === 'Queue',
                        render: this.getDetailPanel,
                    })
                ]}
            />
        );
    };

    private onSearch = () => {
        // @ts-ignore
        this.tableRef.current.retry();
    };

    private getExecutionHistory = async (query: any) => {
        const { page, pageSize, orderBy, orderDirection } = query;
        const { selectedJob, successFilter, buildingFilter, canceledFilter, failedFilter } = this.state;
        const result = await ActionRequest.get(
            `devOps/cloudBuild/getExecutionHistory`,
            {
                page: page + 1,
                perPage: pageSize,
                orderBy: orderBy ? orderBy.field : null,
                orderDirection: orderDirection !== "" ? orderDirection : null,
                selectedJob,
                successFilter,
                buildingFilter,
                canceledFilter,
                failedFilter,
            },
        );

        if (!result) {
            return {
                data: [],
                page: 0,
                totalCount: 0,
            };
        }

        const data = result.jobs.map( (job: any) => {
            return {
                job,
                details: this.getDetails(job),
                target: job.target,
                branch: job.branch,
                duration: job.duration > 0 ? job.duration : moment.now() - job.timestamp,
                actions: this.getActions(job),
            };
        });
        return {
            data,
            page: result.page - 1,
            totalCount: result.totalCount,
        };
    }

    private getDetailPanel = (rowData: any) => {
        const job: Entities.CloudBuildJobExecution = rowData.job;
        return (
            <div style={{padding: '10px 20px'}}>
                <Grid container={true} spacing={1}>
                    <Grid size={{ xs:12, sm:3 }} style={{ fontWeight: 600 }}>
                        Result:
                    </Grid>
                    <Grid size={{ xs: 12, sm: 9 }}>
                        {job.result}
                    </Grid>
                    <Grid size={{ xs:12, sm:3 }} style={{ fontWeight: 600 }}>
                        Build target:
                    </Grid>
                    <Grid size={{ xs: 12, sm: 9 }}>
                        {job.target}
                    </Grid>
                    <Grid size={{ xs:12, sm:3 }} style={{ fontWeight: 600 }}>
                        Started:
                    </Grid>
                    <Grid size={{ xs: 12, sm: 9 }}>
                        {formatDate(job.timestamp)}
                    </Grid>
                    <Grid size={{ xs:12, sm:3 }} style={{ fontWeight: 600 }}>
                        Build time:
                    </Grid>
                    <Grid size={{ xs: 12, sm: 9 }}>
                        {job.duration > 0 ? moment.duration(job.duration).humanize() : moment.duration(moment.now() - job.timestamp).humanize()}
                    </Grid>
                    <Grid size={{ xs:12, sm:3 }} style={{ fontWeight: 600 }}>
                        Branch:
                    </Grid>
                    <Grid size={{ xs: 12, sm: 9 }}>
                        {job.branch}
                    </Grid>
                    <Grid size={{ xs:12, sm:3 }} style={{ fontWeight: 600 }}>
                        Last commit:
                    </Grid>
                    <Grid size={{ xs: 12, sm: 9 }}>
                        {job.commit}
                    </Grid>
                    <Grid size={{ xs:12, sm:3 }} style={{ fontWeight: 600 }}>
                        Clean build:
                    </Grid>
                    <Grid size={{ xs: 12, sm: 9 }}>
                        {job.clean ? 'TRUE' : 'FALSE'}
                    </Grid>
                </Grid>
            </div>
        );
    }

    private getDetails = (details: Entities.CloudBuildJobExecution) => {
        let icon = <div style={{ width: 24, height: 24 }}>
            <CircularProgress style={{ color: '#0073bb', width: 24, height: 24 }} />
            <PlayArrow style={{ position: 'relative', color: '#0073bb', left: 4, top: -25, width: 16, height: 16 }}/>
        </div>;
        switch (details.result) {
            case 'Queue':
                icon = <CircularProgress style={{ color: '#9e9e9e', width: 24, height: 24 }} />;
                break;
            case 'Success':
                icon = <CheckCircle style={{ color: '#4eb709', width: 24, height: 24 }}/>;
                break;
            case 'Failed':
                icon = <ErrorIcon style={{ color: '#d20f08', width: 24, height: 24 }}/>;
                break;
            case 'Canceled':
                icon = <RemoveCircle style={{ color: '#e39f28', width: 24, height: 24}}/>;
                break;

        }
        return (
            <div style={{ display: 'flex', gridAutoColumns: 'column', gridColumnGap: 10, justifyContent: 'flex-start', alignItems: 'center' }}>
                {icon}
                {`#${details.number}`}
            </div>
        );
    }

    private getActions = (details: Entities.CloudBuildJobExecution) => {
        const showDetails = () => {
            this.toLink(`/cloudBuild/details/${details.id}/${details.number}`);
        };

        if (details.result === 'Queue') {
            return <></>;
        }

        const openMenu = (event: any) => this.setState({ authAnchorEl: event.currentTarget, selectedDetails: details });
        return (
            <div>
                <Button text="Details" onClick={showDetails} variant="text" style={{ margin: 'auto 0px', color: '#1a73e8' }}/>
                <IconButton
                    aria-owns={'menu-appbar'}
                    aria-haspopup="true"
                    onClick={openMenu}
                    color="inherit"
                >
                    <MoreHoriz style={{ width: 18, height: 18 }}/>
                </IconButton>
            </div>
        );
    }

    private renderBuildDetailsMenu = () => {
        const { authAnchorEl, selectedDetails } = this.state;
        if (!selectedDetails) {
            return;
        }

        const closeMenu = () => this.setState({ authAnchorEl: undefined, selectedDetails: null });
        const open = !!authAnchorEl;

        const onCancel = async () => {
            closeMenu();
            await actions.devOps.cancelCloudBuild(selectedDetails.id, selectedDetails.number);
            this.onSearch();
        };

        const downloadItems = _.map(selectedDetails.artifacts, (artifact) => {
            const handleDownload = async () => {
                // if (selectedDetails.type === 'UnityCloud') {
                    // @ts-ignore
                    document.getElementById(artifact.file.fileName).click();
                /*
                }

                await actions.devOps.downloadArchive(selectedDetails.id, selectedDetails.number, artifact.file.relativePath);

                 */
            };

            return (
                <>
                    <a href={artifact.file.href} id={artifact.file.fileName} hidden={true} download={true} />
                    <MenuItem color="inherit" key={artifact.name} value={artifact.name} onClick={handleDownload} style={{ padding: '10px 24px' }} >
                        <GetAppIcon style={{ width: 16, height: 16, marginRight: 10 }} />
                        {`Download ${artifact.name}`}
                    </MenuItem>
                </>
            );
        });

        return (
            <BuildDetailsMenu
                anchorEl={authAnchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={open}
                onClose={closeMenu}
            >
                {(selectedDetails.result === 'Building' || selectedDetails.result === 'Queue') && (
                    <MenuItem color="inherit" key={'cancel'} value={'cancel'} onClick={onCancel} style={{ padding: '10px 24px' }}>
                        <CancelIcon style={{ width: 16, height: 16, marginRight: 10 }} />
                        Cancel
                    </MenuItem>
                )}
                {downloadItems}
            </BuildDetailsMenu>
        );
    }

    private openBuildDialog = () => {
        this.stopTimer();
        this.setState({ openDialog: 'Build' });
    }
    private renderBuildDialog = () => {
        const { jobs } = this.props;
        const { openDialog } = this.state;
        return (
            <StartBuildDialog
                open={openDialog === 'Build'}
                styled={this.props.styled}
                title="Start Build"
                jobs={jobs}
                onClose={this.closeDialog}
                onStartBuild={this.onStartBuild}
            />
        );
    };
    private onStartBuild = async (jobs: Entities.CloudBuildJob[], clean: boolean, branch: string) => {
        const ids = jobs.map(j => j.id);
        await actions.devOps.startCloudBuild(ids, clean, branch);
    }

    private closeDialog = () => {
        this.setState({
            openDialog: null,
        });
        this.startTimer();
    }

    private onRefreshData = (oldData:any[], newData: any[]) => {
        if (!this.tableRef) {
            return;
        }
        const current = this.tableRef.current;
        if  (!current) {
            return;
        }

        if (!oldData.some( d => d.job.result === 'Building' || d.job.result === 'Queue')) {
            return;
        }

        if(!oldData || !newData) {
            return;
        }

        let someFinished = false;
        oldData.forEach( oldJob => {
            if(oldJob.job.result !== 'Building' && oldJob.job.result !== 'Queue') {
                return;
            }

            const newJob = newData.find(n => n.job.id === oldJob.job.id);
            if (!newJob) {
                return;
            }

            if (oldJob.job.result === 'Building' && newJob.job.result !== 'Building') {
                someFinished = true;
            }
            if (oldJob.job.result === 'Queue' && newJob.job.result !== 'Queue') {
                someFinished = true;
            }
        });
        if(!someFinished) {
            return;
        }

        const dataManager = current.dataManager;
        dataManager.setData(newData);
        current.setState({ ...dataManager.getRenderState() });
    }

    private onRefresh = () => {
        if (!this.tableRef) {
            return;
        }
        const current = this.tableRef.current;
        if  (!current) {
            return;
        }

        const dataManager = current.dataManager;
        const data = dataManager.data;
        if (data.length === 0) {
            return;
        }

        if (!data.some( (d: any) => d.job.result === 'Building' || d.job.result === 'Queue')) {
            return;
        }

        data.forEach((d: any) => {
            if(d.job.result === 'Building' || d.job.result === 'Queue') {
                d.duration+=1000;
            }
        });

        dataManager.setData(data);
        current.setState({ ...dataManager.getRenderState() });
    };

    private startTimer = () => {
        this.timer = setInterval(this.onRefresh, 1000);
    }

    private stopTimer = () => {
        if (this.timer) {
            clearInterval(this.timer);
        }
    }
}

const BuildDetailsMenu = styled(Menu)(({
    '& .MuiMenu-paper': {
        marginTop: 0,
    },
}));

const mapStateToProps = (state: StateMap): Props => ({
    ...mapProps(state),
    jobs: state.devOps.jobs,
});
const AppContainer = (props: Props) =>
{
    const smallUp = useMediaQuery((theme) => theme.breakpoints.up('sm'));
    const navigate = useNavigate();
    return (<Container {...props} styled={{themeMode: props.app.themeMode, smallUp}} navigate={navigate}/>);
};
export default connect(mapStateToProps)(AppContainer);
